import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Trash2 } from "lucide-react";
import { useCart } from "@/lib/useCart";
import styles from "./CartDrawer.module.css";

/**
 * Slide-out cart drawer (from the right). Opened by the ShopCartPill instead of
 * navigating to the full /shop/cart page. Stays mounted so it can animate in/out
 * and show an empty state if the last item is removed while open.
 *
 * Rendered through a portal to <body>: the shop pages live inside
 * PageTransition's motion.div, whose `willChange: transform` makes it the
 * containing block for every `position: fixed` descendant. Without the portal,
 * `.root { position: fixed; inset: 0 }` would resolve against that full-page
 * wrapper (thousands of px tall) instead of the viewport, so the drawer's
 * height became page-height and the checkout footer fell far below the fold.
 * Portaling to <body> escapes the transformed ancestor and restores true
 * viewport-relative fixed positioning.
 */
export default function CartDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lines, subtotal, setQty, remove } = useCart();
  const [mounted, setMounted] = useState(false);

  // Portal target (document.body) only exists on the client. Gate rendering
  // until after hydration so SSR/prerender and first client render both emit
  // nothing (matching markup, no hydration mismatch).
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div className={`${styles.root} ${open ? styles.open : ""}`} aria-hidden={!open}>
      <div className={styles.overlay} onClick={onClose} />
      <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Cart">
        <header className={styles.header}>
          <span className={styles.title}>Cart</span>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close cart">
            <X size={18} />
          </button>
        </header>

        {lines.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyText}>Your cart is empty.</p>
            <a className={styles.keepShopping} href="/shop" onClick={onClose}>Browse the shop →</a>
          </div>
        ) : (
          <>
            <ul className={styles.lines}>
              {lines.map((l) => (
                <li key={`${l.productId}:${l.variantId}`} className={styles.line}>
                  <img className={styles.thumb} src={l.image} alt={l.title} width={64} height={64} />
                  <div className={styles.meta}>
                    <span className={styles.name}>{l.title}</span>
                    <span className={styles.variant}>{l.variantLabel}</span>
                    <div className={styles.controls}>
                      <input
                        className={styles.qty}
                        type="number" min={1} max={99} value={l.qty}
                        onChange={(e) => setQty(l.productId, l.variantId, Number(e.target.value))}
                        aria-label={`Quantity for ${l.title}`}
                      />
                      <span className={styles.lineTotal}>${(l.unitPrice * l.qty).toFixed(2)}</span>
                      <button
                        type="button"
                        className={styles.remove}
                        onClick={() => remove(l.productId, l.variantId)}
                        aria-label={`Remove ${l.title}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className={styles.footer}>
              <div className={styles.subtotal}>Subtotal <strong data-testid="cart-subtotal">${subtotal.toFixed(2)}</strong></div>
              <p className={styles.note}>Shipping &amp; tax calculated at checkout.</p>
              <a className={styles.checkout} href="/shop/checkout">Proceed to checkout →</a>
            </div>
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}
